import { useContext, useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import { LayoutContext } from '../../context';
import { StoryAvatar } from './StoriesBar';

const MotionBox = motion(Box);

export default function DesktopStoriesWidget() {
    const {
        storiesBar, loadStoriesBar, newsStories, setNewsStories,
    } = useContext(LayoutContext);

    const items = storiesBar?.items || [];
    const storiesOpen = !!newsStories?.open;
    const [expanded, setExpanded] = useState(false);

    // Подгружаем при монтировании и при каждом закрытии плеера (обновить «просмотрено»).
    useEffect(() => { if (!storiesOpen) loadStoriesBar?.(); }, [storiesOpen, loadStoriesBar]);

    // Сколько ещё не просмотрено — для счётчика на свёрнутой пилюле.
    const unseen = items.filter((it) => !it?.view_state?.is_completed).length;

    const openAt = (idx) => setNewsStories({ open: true, items, startIndex: idx, preview: false });

    // Нет сторис — ничего не показываем.
    if (!items.length) return null;

    return (
        // Десктоп-только: на мобиле сторис живут в шапке (StoriesRow).
        // Отступы/позицию задаёт ПК-шапка (HeaderLK) — здесь их не добавляем.
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', minWidth: 0 }}>
            <MotionBox
                layout
                transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                animate={{ borderRadius: expanded ? 20 : 999 }}
                sx={{
                    display: 'inline-flex',
                    alignItems: expanded ? 'flex-start' : 'center',
                    py: 1,          // отступы сверху/снизу — по просьбе
                    px: 0,          // без боковых полей: горизонтальные отступы задаёт шапка
                    maxWidth: '100%',
                    // без фона/бордера/тени/скругления — только текст и сами сторисы
                    position: 'relative',
                    overflow: 'visible',
                    cursor: expanded ? 'default' : 'pointer',
                }}
                onClick={() => { if (!expanded) setExpanded(true); }}
                whileHover={!expanded ? { scale: 1.02 } : undefined}
                whileTap={!expanded ? { scale: 0.98 } : undefined}
            >
                {/* Фонового свечения нет — блок без подложки, только текст и сторисы */}

                <AnimatePresence mode="wait" initial={false}>
                    {!expanded ? (
                        // ── Свёрнутое состояние: стопка из 3 кружков + подпись + счётчик ──
                        <MotionBox
                            key="collapsed"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, position: 'relative', zIndex: 1 }}
                        >
                            <Box sx={{ display: 'flex' }}>
                                {items.slice(0, 3).map((it, i) => (
                                    <Box key={it.id} sx={{ ml: i === 0 ? 0 : '-12px', zIndex: 3 - i }}>
                                        <StoryAvatar item={it} size={34} onClick={(e) => { e.stopPropagation(); openAt(i); }} />
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ pr: 0.5 }}>
                                <Typography level="title-sm" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                    Истории
                                </Typography>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', lineHeight: 1.2 }}>
                                    {unseen > 0 ? `${unseen} новых` : 'всё просмотрено'}
                                </Typography>
                            </Box>
                            <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: 'primary.400' }} />
                        </MotionBox>
                    ) : (
                        // ── Раскрытое состояние: горизонтальная лента всех кружков ──
                        <MotionBox
                            key="expanded"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}
                        >
                            <Box sx={{
                                display: 'flex', gap: 1.25, overflowX: 'auto', py: 0.5, pr: 1,
                                maxWidth: 'calc(100vw - 360px)',
                                '::-webkit-scrollbar': { height: 6 },
                                '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 6 },
                            }}>
                                {items.map((it, i) => (
                                    <MotionBox
                                        key={it.id}
                                        initial={{ opacity: 0, scale: 0.6, y: 8 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        transition={{ type: 'spring', stiffness: 400, damping: 24, delay: Math.min(i * 0.04, 0.4) }}
                                        sx={{ textAlign: 'center', width: 66, flex: '0 0 auto' }}
                                    >
                                        <StoryAvatar item={it} size={52} onClick={() => openAt(i)} />
                                        <Typography level="body-xs" noWrap
                                            sx={{ maxWidth: 62, mx: 'auto', color: 'text.secondary', mt: 0.5 }}>
                                            {it.title}
                                        </Typography>
                                    </MotionBox>
                                ))}
                            </Box>

                            <IconButton
                                size="sm" variant="soft" color="neutral"
                                onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
                                sx={{ borderRadius: '50%', flexShrink: 0, alignSelf: 'flex-start', mt: 0.5 }}
                            >
                                <CloseRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </MotionBox>
                    )}
                </AnimatePresence>
            </MotionBox>
        </Box>
    );
}